import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from 'react-md';
import { Accordion, AccordionDetails, AccordionSummary } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { isEqual } from 'lodash-es';

import { fromJS } from 'immutable';

import cn from 'classnames';

import CoverageBlock from './CoverageBlock';
import UnderwritingsBlock from './UnderwritingsBlock';
import DocumentsBlock from './DocumentsBlock';
import BeneficiariesBlock from './BeneficiariesBlock';
import EventsBlock from './EventsBlock';
import NotesBlock from './NotesBlock';
// import AttachmentsBlock from './AttachmentsBlock';

import { ciEquals } from '../../Utils/Utils';

const EmptyItem = () => (
  <div className="item empty">
    <div className="label" />
    <div className="value">
      <CircularProgress
        id="summary-empty-item"
        className="loading-anim-offer"
        scale={0.5}
        style={{ margin: '2em auto' }}
      />
    </div>
  </div>
);

class ControlledExpansionPanels extends Component {
  constructor(props) {
    super(props);

    this.state = {
      contract: props.contract,
      panels: fromJS(props.panels),
    };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let nextState = null;
    const prevContract = prevState.contract;
    const { contract: nextContract } = nextProps;
    if (typeof prevContract.id !== 'undefined') {
      if (nextContract && !isEqual(nextContract, prevContract)) {
        nextState = { contract: { id: prevContract.id, ...nextContract } };
      }
    }
    if (!isEqual(nextProps.loading, prevState.loading)) {
      nextState = { ...nextState, loading: nextProps.alerts };
    }
    return nextState;
  }

  componentDidMount() {
    const { hasMissingBlockerDocument } = this.props;
    if (hasMissingBlockerDocument) {
      const { panels } = this.state;
      this.setState({
        panels: panels.setIn(['documents', 'expanded'], true),
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const self = this;
    const { hasMissingBlockerDocument } = prevProps;
    const { panels } = prevState;
    //
    if (hasMissingBlockerDocument && panels) {
      if (!panels.getIn(['documents', 'expanded'])) {
        self.setState({
          panels: panels.setIn(['documents', 'expanded'], true),
        });
      }
    }
  }

  isExpanded = panel => {
    const { panels } = this.state;
    const expanded = panels.getIn([panel, 'expanded']);
    return !!expanded;
  };

  handleChange = panel => (_event, expanded) => {
    const { panels } = this.state;
    this.setState({
      panels: panels.setIn([panel, 'expanded'], expanded),
    });
  };

  render() {
    const { strings, loading, frequency, clauses, hasMissingBlockerDocument, createNote } = this.props;
    const { contract } = this.state;
    const expandedIcon = () => <ExpandMoreIcon />;

    const coverageDetails = () =>
      loading && loading.isLoadingContract ? (
        <EmptyItem />
      ) : (
        <CoverageBlock contract={contract} loading={loading} strings={strings} frequency={frequency} />
      );

    const beneficiariesDetails = () =>
      loading && loading.isLoadingContract && typeof contract.premiumsContract === 'undefined' ? (
        <EmptyItem />
      ) : (
        <BeneficiariesBlock contract={contract} loading={loading} strings={strings} clauses={clauses} />
      );

    const documentsDetails = () => {
      const { deleteContractDocument, addContractDocument, submitQuote, updateDocumentRequestLine } = this.props;
      return loading && loading.isLoadingContract && typeof contract.premiumsContract === 'undefined' ? (
        <EmptyItem />
      ) : (
        <DocumentsBlock
          contract={contract}
          loading={loading}
          strings={strings}
          deleteContractDocument={deleteContractDocument}
          addContractDocument={addContractDocument}
          updateDocumentRequestLine={updateDocumentRequestLine}
          submitQuote={submitQuote}
        />
      );
    };

    const eventsDetails = () =>
      loading && loading.isLoadingContract && typeof contract.events === 'undefined' ? (
        <EmptyItem />
      ) : (
        <EventsBlock contract={contract} loading={loading} strings={strings} />
      );

    const notesDetails = () =>
      loading && loading.isLoadingContract && typeof contract.notes === 'undefined' ? (
        <EmptyItem />
      ) : (
        <NotesBlock contract={contract} loading={loading} strings={strings} createNote={createNote} />
      );

    const underwritingsDetails = () =>
      loading && loading.isLoadingContract ? (
        <EmptyItem />
      ) : (
        <UnderwritingsBlock contract={contract} loading={loading} strings={strings} />
      );

    const RequiredPanel = ({ name, defaultTitle, details, classes }) => (
      <Accordion
        expanded={this.isExpanded(name)}
        onChange={this.handleChange(name)}
        className={cn({
          panel: true,
          [name]: true,
          [classes]: classes,
          expanded: this.isExpanded(name),
        })}
      >
        <AccordionSummary className="summary" expandIcon={expandedIcon()}>
          <Typography className="title">{strings[`contract.block.${name}.title`] || defaultTitle}</Typography>
        </AccordionSummary>
        <AccordionDetails className="details">{details()}</AccordionDetails>
      </Accordion>
    );

    const OptionalPanel = ({ name, details, defaultTitle, condition, warning }) => {
      if (typeof condition !== 'undefined' && condition) {
        return (
          <Accordion
            expanded={this.isExpanded(name)}
            onChange={this.handleChange(name)}
            className={cn('panel', { name: true }, { warning }, { expanded: this.isExpanded(name) })}
          >
            <AccordionSummary className="summary" expandIcon={expandedIcon()}>
              <Typography className="title">{strings[`contract.block.${name}.title`] || defaultTitle}</Typography>
            </AccordionSummary>
            <AccordionDetails className="details">{details()}</AccordionDetails>
          </Accordion>
        );
      }
      return loading && loading.isLoadingContract ? <EmptyItem /> : '';
    };

    return (
      <div className="controlled-expansion-panel">
        <RequiredPanel name="coverage" details={coverageDetails} defaultTitle="Coverage" />
        <OptionalPanel
          name="beneficiaries"
          details={beneficiariesDetails}
          defaultTitle="Beneficiaries"
          condition={Array.isArray(contract.beneficiaries) && contract.beneficiaries.length}
        />
        <RequiredPanel
          name="documents"
          details={documentsDetails}
          defaultTitle="Documents"
          classes={hasMissingBlockerDocument ? 'missing-blocker-document' : undefined}
        />
        <OptionalPanel
          name="underwritings"
          details={underwritingsDetails}
          defaultTitle="Risk Analysis"
          warning={false}
          condition={
            contract &&
            ciEquals('quote', contract.status) &&
            Array.isArray(contract.underwritings) &&
            contract.underwritings.length
          }
        />
        <RequiredPanel name="events" details={eventsDetails} defaultTitle="Events" />
        <RequiredPanel name="notes" details={notesDetails} defaultTitle="Notes" />
        {/* <RequiredPanel name="attachments" details={attachmentsDetails} defaultTitle="attachments" /> */}
      </div>
    );
  }
}

ControlledExpansionPanels.propTypes = {
  strings: PropTypes.objectOf(PropTypes.string),
  loading: PropTypes.objectOf(PropTypes.bool).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  contract: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  panels: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  frequency: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  clauses: PropTypes.array.isRequired,
  hasMissingBlockerDocument: PropTypes.bool,
  deleteContractDocument: PropTypes.func.isRequired,
  addContractDocument: PropTypes.func.isRequired,
  updateDocumentRequestLine: PropTypes.func.isRequired,
  submitQuote: PropTypes.func.isRequired,
  createNote: PropTypes.func.isRequired,
  cryptologSign: PropTypes.func.isRequired,
  cryptologCheck: PropTypes.func.isRequired,
};

ControlledExpansionPanels.defaultProps = {
  strings: {},
  panels: {
    coverage: { expanded: true },
    ongoingProcess: { expanded: true },
    underwritings: { expanded: true },
    beneficiaries: { expanded: true },
    documents: { expanded: true },
    events: { expanded: true },
    notes: { expanded: true },
    attachments: { expanded: true },
  },
  hasMissingBlockerDocument: false,
};

export default ControlledExpansionPanels;
